import React from 'react';

type SvgIcon = React.ComponentType<React.SVGProps<SVGSVGElement>>;

type Size = 'sm' | 'md' | 'lg';
type Pad = 'sm' | 'md' | 'lg';
type Weight = 'normal' | 'medium' | 'semibold' | 'bold';
type Variant = 'filled' | 'outline' | 'ghost';
type Color = 'primary' | 'gray' | 'white';
type HoverMode = 'none' | 'shadow' | 'tint';
type IconPosition = 'left' | 'right';
type Radius = 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
type Width =
  | 'auto'
  | 'full'
  | 'fit'
  | 'max'
  | 'min'
  | '1/2'
  | '1/3'
  | '2/3'
  | '1/4'
  | '2/4'
  | '3/4'
  | '1/5'
  | '2/5'
  | '3/5'
  | '4/5';

const sizeToText: Record<Size, string> = {
  sm: 'text-sm',
  md: 'text-base',
  lg: 'text-lg',
};
const sizeToIcon: Record<Size, number> = { sm: 16, md: 18, lg: 20 };
const padToClass: Record<Pad, string> = { sm: 'p-3', md: 'p-4', lg: 'p-6' };
const weightToClass: Record<Weight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};
const radiusToClass: Record<Radius, string> = {
  none: 'rounded-none',
  sm: 'rounded-sm',
  md: 'rounded-md',
  lg: 'rounded-lg',
  xl: 'rounded-xl',
  '2xl': 'rounded-2xl',
  full: 'rounded-full',
};
const widthToClass: Record<Width, string> = {
  auto: 'w-auto',
  full: 'w-full',
  fit: 'w-fit',
  max: 'w-max',
  min: 'w-min',
  '1/2': 'w-1/2',
  '1/3': 'w-1/3',
  '2/3': 'w-2/3',
  '1/4': 'w-1/4',
  '2/4': 'w-2/4',
  '3/4': 'w-3/4',
  '1/5': 'w-1/5',
  '2/5': 'w-2/5',
  '3/5': 'w-3/5',
  '4/5': 'w-4/5',
};

const palette: Record<
  Color,
  Record<
    Variant,
    { base: string; hover: string; focusRing: string; icon: string }
  >
> = {
  primary: {
    filled: {
      base: 'bg-primary-dark text-white',
      hover: 'hover:bg-primary-medium',
      focusRing: 'focus-visible:ring-2 focus-visible:ring-primary-light',
      icon: 'text-white',
    },
    outline: {
      base: 'bg-transparent text-primary-medium border-2 border-primary-medium',
      hover: 'hover:bg-primary-light/20',
      focusRing: 'focus-visible:ring-2 focus-visible:ring-primary-light',
      icon: 'text-primary-medium',
    },
    ghost: {
      base: 'bg-transparent text-primary-medium',
      hover: 'hover:bg-primary-light/20',
      focusRing: 'focus-visible:ring-2 focus-visible:ring-primary-light',
      icon: 'text-primary-medium',
    },
  },
  gray: {
    filled: {
      base: 'bg-gray-dark text-white',
      hover: 'hover:bg-gray-medium',
      focusRing: 'focus-visible:ring-2 focus-visible:ring-gray-medium-light',
      icon: 'text-white',
    },
    outline: {
      base: 'bg-transparent text-gray-dark border-2 border-gray-medium-light',
      hover: 'hover:bg-gray-light',
      focusRing: 'focus-visible:ring-2 focus-visible:ring-gray-medium-light',
      icon: 'text-gray-dark',
    },
    ghost: {
      base: 'bg-transparent text-gray-dark',
      hover: 'hover:bg-gray-light',
      focusRing: 'focus-visible:ring-2 focus-visible:ring-gray-medium-light',
      icon: 'text-gray-dark',
    },
  },
  white: {
    filled: {
      base: 'bg-white text-gray-dark',
      hover: 'hover:shadow-lg',
      focusRing: 'focus-visible:ring-2 focus-visible:ring-primary-light',
      icon: 'text-gray-dark',
    },
    outline: {
      base: 'bg-white text-gray-dark border-2 border-gray-disabled',
      hover: 'hover:shadow-md',
      focusRing: 'focus-visible:ring-2 focus-visible:ring-primary-light',
      icon: 'text-gray-dark',
    },
    ghost: {
      base: 'bg-transparent text-gray-dark',
      hover: 'hover:bg-gray-light',
      focusRing: 'focus-visible:ring-2 focus-visible:ring-primary-light',
      icon: 'text-gray-dark',
    },
  },
};

export interface TileButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  label?: string;
  Icon?: SvgIcon;
  iconPosition?: IconPosition;
  iconGap?: number;
  size?: Size;
  pad?: Pad;
  weight?: Weight;
  variant?: Variant;
  color?: Color;
  hover?: HoverMode;
  radius?: Radius;
  /** Usa utilidades Tailwind predefinidas (tiene prioridad sobre `fullWidth`) */
  width?: Width;
  /** CSS width directo (p.ej. '33%' o 240). Si se pasa, sobrescribe todo. */
  styleWidth?: string | number;
  fullWidth?: boolean;
  href?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  className?: string;
  disabled?: boolean;
}

const TileButton: React.FC<TileButtonProps> = ({
  label = '',
  Icon,
  iconPosition = 'left',
  iconGap = 12,
  size = 'lg',
  pad = 'lg',
  weight = 'medium',
  variant = 'filled',
  color = 'white',
  hover = 'shadow',
  radius = 'none',
  width,
  styleWidth,
  fullWidth = false,
  href,
  className = '',
  onClick,
  disabled = false,
  ...rest
}) => {
  const paletteSet = palette[color][variant];

  const hoverClass = disabled
    ? ''
    : hover === 'shadow'
    ? paletteSet.hover
    : hover === 'tint'
    ? 'hover:bg-primary-light/20'
    : '';

  const widthClass =
    styleWidth != null
      ? ''
      : width
      ? widthToClass[width]
      : fullWidth
      ? 'w-full'
      : '';

  const disabledClasses = disabled
    ? 'cursor-not-allowed pointer-events-none bg-gray-disabled'
    : '';

  const cls = [
    'inline-flex items-center justify-center transition-all duration-200 select-none',
    radiusToClass[radius],
    widthClass,
    sizeToText[size],
    padToClass[pad],
    weightToClass[weight],
    paletteSet.base,
    paletteSet.focusRing,
    hoverClass,
    className,
    disabledClasses,
  ]
    .filter(Boolean)
    .join(' ');

  const iconSize = sizeToIcon[size];
  const iconClass = paletteSet.icon;

  const content = (
    <>
      {Icon && iconPosition === 'left' && (
        <Icon
          aria-hidden="true"
          className={iconClass}
          width={iconSize}
          height={iconSize}
        />
      )}
      {label && <span>{label}</span>}
      {Icon && iconPosition === 'right' && (
        <Icon
          aria-hidden="true"
          className={iconClass}
          width={iconSize}
          height={iconSize}
        />
      )}
    </>
  );

  const style: React.CSSProperties = {
    gap: iconGap,
    ...(styleWidth != null ? { width: styleWidth } : null),
  };

  if (href) {
    return (
      <a
        href={disabled ? undefined : href}
        className={cls}
        style={style}
        onClick={
          disabled
            ? undefined
            : (onClick as React.MouseEventHandler<HTMLAnchorElement>)
        }
      >
        {content}
      </a>
    );
  }

  return (
    <button
      type="button"
      className={cls}
      style={style}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      {...rest}
    >
      {content}
    </button>
  );
};

export default TileButton;
